import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, CircleNotch, Clock } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function PaymentResult({ status }) {
  const navigate = useNavigate();
  const [state, setState] = useState(status === "cancel" ? "cancelled" : "checking");
  const [order, setOrder] = useState(null);

  useEffect(() => { document.title = "Payment | Zaekids Laundromat"; }, []);

  useEffect(() => {
    if (status === "cancel") {
      const params = new URLSearchParams(window.location.search);
      setOrder(params.get("order"));
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        setOrder(data.order_number);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch { /* keep trying */ }
      if (active && tries++ < 8) setTimeout(poll, 2000);
      else if (active) setState("pending");
    };
    poll();
    return () => { active = false; };
  }, [status]);

  const config = {
    checking: { icon: CircleNotch, spin: true, title: "Confirming your payment…", desc: "Just a moment while we check with our payment provider.", color: "text-brand-green" },
    success: { icon: CheckCircle, title: "Payment received! 💛", desc: `Thank you — your payment for order ${order || ""} is complete.`, color: "text-brand-green" },
    pending: { icon: Clock, title: "Payment is processing", desc: "This can take a moment. You'll get a confirmation once it clears.", color: "text-amber-500" },
    cancelled: { icon: XCircle, title: "Payment cancelled", desc: "No worries — your order is still saved. You can pay any time.", color: "text-slate-400" },
    error: { icon: XCircle, title: "Something went wrong", desc: "We couldn't confirm this payment. Please try again from the Track Order page.", color: "text-red-500" },
  };
  const c = config[state] || config.error;
  const Icon = c.icon;

  return (
    <div data-testid="payment-result-page" className="mx-auto max-w-xl px-4 py-24 text-center sm:px-6">
      <span className={`mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-soft-green ${c.color}`}>
        <Icon size={56} weight="fill" className={c.spin ? "animate-spin" : ""} />
      </span>
      <h1 data-testid="payment-result-title" className="mt-6 text-3xl font-black text-brand-green sm:text-4xl">{c.title}</h1>
      <p className="mt-3 text-slate-600">{c.desc}</p>
      <div className="mt-8 flex flex-wrap justify-center gap-4">
        <button data-testid="payment-track-btn" onClick={() => navigate("/track", order ? { state: { order_number: order } } : undefined)}
          className="btn-bounce rounded-full bg-brand-green px-8 py-4 font-extrabold text-white">Track My Order</button>
        <button onClick={() => navigate("/")} className="btn-bounce rounded-full border-2 border-brand-green px-8 py-4 font-extrabold text-brand-green">Back Home</button>
      </div>
    </div>
  );
}
